import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import TeamDirectorySection from './TeamDirectorySection';
import { apiRequest } from '../../../api/client';
import '../quotes/quote-generator.css';
import './settings.css';

export type InvitationRow = {
  _id: string;
  name: string;
  email: string;
  role: 'admin' | 'technician';
  status: 'pending' | 'accepted' | 'denied' | 'expired';
  expiresAt: string;
  createdAt: string;
};

type InviteFormState = {
  name: string;
  email: string;
  role: 'admin' | 'technician';
};

const defaultForm: InviteFormState = {
  name: '',
  email: '',
  role: 'technician'
};

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function stripApiError(message: string): string {
  return message.replace(/^HTTP \d+[^:]+:\s*/, '');
}

function formatInviteError(message: string): string {
  const cleaned = stripApiError(message);
  if (/smtp login failed|app password|smtp_user|smtp_pass/i.test(cleaned)) {
    return cleaned;
  }
  if (/535|BadCredentials|Username and Password not accepted/i.test(cleaned)) {
    return 'Email could not be sent: Gmail rejected the SMTP login. Update SMTP_PASS in backend/.env.local with a new App Password, then restart the backend.';
  }
  return cleaned;
}

function roleLabel(role: string): string {
  return role === 'admin' ? 'Admin' : 'Technician';
}

function statusBadgeClass(status: InvitationRow['status']): string {
  switch (status) {
    case 'pending':
      return 'settings-invite-badge settings-invite-badge--pending';
    case 'accepted':
      return 'settings-invite-badge settings-invite-badge--accepted';
    case 'denied':
      return 'settings-invite-badge settings-invite-badge--denied';
    default:
      return 'settings-invite-badge settings-invite-badge--expired';
  }
}

function statusLabel(status: InvitationRow['status']): string {
  switch (status) {
    case 'pending':
      return 'Pending';
    case 'accepted':
      return 'Accepted';
    case 'denied':
      return 'Denied';
    default:
      return 'Expired';
  }
}

export default function TeamSettingsTab(): React.JSX.Element {
  const [form, setForm] = useState<InviteFormState>(defaultForm);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [invitations, setInvitations] = useState<InvitationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [actionId, setActionId] = useState<string | null>(null);
  const [cancelConfirmId, setCancelConfirmId] = useState<string | null>(null);
  const [directoryRefreshKey, setDirectoryRefreshKey] = useState(0);

  const loadInvitations = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const res = await apiRequest<{ data: InvitationRow[] }>('/api/invitations');
      setInvitations(res.data || []);
    } catch (e: unknown) {
      setListError(e instanceof Error ? e.message : 'Failed to load invitations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadInvitations();
  }, [loadInvitations]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setEmailError(null);
    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      await apiRequest('/api/invitations', {
        method: 'POST',
        json: form
      });
      setSubmitSuccess(`Invitation sent to ${form.email.trim()}`);
      setForm(defaultForm);
      await loadInvitations();
      setDirectoryRefreshKey((k) => k + 1);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to send invitation.';
      if (message.includes('409') && message.toLowerCase().includes('already exists')) {
        setEmailError('A user with this email already exists.');
      } else if (message.includes('409') && message.toLowerCase().includes('active invitation')) {
        setEmailError('An active invitation already exists for this email.');
      } else if (message.includes('409')) {
        setEmailError(formatInviteError(message));
      } else {
        setSubmitError(formatInviteError(message));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async (id: string) => {
    setActionId(id);
    try {
      await apiRequest(`/api/invitations/${id}/resend`, { method: 'POST' });
      await loadInvitations();
    } catch (e: unknown) {
      setListError(e instanceof Error ? e.message : 'Failed to resend invitation.');
    } finally {
      setActionId(null);
    }
  };

  const handleCancel = async (id: string) => {
    setActionId(id);
    try {
      await apiRequest(`/api/invitations/${id}`, { method: 'DELETE' });
      setCancelConfirmId(null);
      await loadInvitations();
    } catch (e: unknown) {
      setListError(e instanceof Error ? e.message : 'Failed to cancel invitation.');
    } finally {
      setActionId(null);
    }
  };

  return (
    <div className="settings-tab-stack">
      <section className="quote-card settings-section-card">
        <div className="quote-section-title">Invite new member</div>
        <p className="settings-hint">Send an email invitation to add an admin or technician to your workspace.</p>

        <form className="settings-invite-form" onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label" htmlFor="invite-name">
                Name
              </label>
              <input
                id="invite-name"
                className="form-input"
                value={form.name}
                required
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="invite-email">
                Email
              </label>
              <input
                id="invite-email"
                className="form-input"
                type="email"
                value={form.email}
                required
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, email: e.target.value }));
                  setEmailError(null);
                }}
              />
              {emailError ? <p className="settings-field-error">{emailError}</p> : null}
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="invite-role">
                Role
              </label>
              <select
                id="invite-role"
                className="form-input"
                value={form.role}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    role: e.target.value as InviteFormState['role']
                  }))
                }
              >
                <option value="technician">Technician</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>

          {submitError ? <p className="settings-field-error">{submitError}</p> : null}
          {submitSuccess ? <p className="settings-field-success">{submitSuccess}</p> : null}

          <button type="submit" className="btn-primary settings-inline-action" disabled={submitting}>
            {submitting ? 'Sending…' : 'Send Invitation'}
          </button>
        </form>
      </section>

      <section className="quote-card settings-section-card">
        <div className="quote-section-title">Sent invitations</div>
        <p className="settings-hint">Track pending, accepted, and expired invitations.</p>

        {listError ? <p className="settings-field-error">{listError}</p> : null}

        {loading ? (
          <div className="settings-invite-skeleton" aria-hidden>
            <div className="settings-invite-skeleton-row" />
            <div className="settings-invite-skeleton-row" />
            <div className="settings-invite-skeleton-row" />
          </div>
        ) : invitations.length === 0 ? (
          <p className="settings-placeholder-text">No invitations sent yet.</p>
        ) : (
          <div className="settings-invite-table-wrap">
            <table className="settings-invite-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Expires</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invitations.map((row) => (
                  <tr key={row._id}>
                    <td>{row.name}</td>
                    <td>{row.email}</td>
                    <td>{roleLabel(row.role)}</td>
                    <td>
                      <span className={statusBadgeClass(row.status)}>{statusLabel(row.status)}</span>
                    </td>
                    <td>{formatDate(row.expiresAt)}</td>
                    <td>
                      <div className="settings-invite-actions">
                        {row.status === 'pending' || row.status === 'expired' ? (
                          <button
                            type="button"
                            className="settings-text-btn"
                            disabled={actionId === row._id}
                            onClick={() => void handleResend(row._id)}
                          >
                            {actionId === row._id ? 'Working…' : 'Resend'}
                          </button>
                        ) : null}
                        {row.status === 'pending' ? (
                          cancelConfirmId === row._id ? (
                            <div className="settings-inline-confirm settings-inline-confirm--table">
                              <p className="settings-inline-confirm-text">Cancel this invitation?</p>
                              <div className="settings-inline-confirm-actions">
                                <button
                                  type="button"
                                  className="btn-secondary"
                                  disabled={actionId === row._id}
                                  onClick={() => void handleCancel(row._id)}
                                >
                                  Confirm
                                </button>
                                <button
                                  type="button"
                                  className="settings-text-btn"
                                  disabled={actionId === row._id}
                                  onClick={() => setCancelConfirmId(null)}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="settings-text-btn settings-text-btn--danger"
                              disabled={actionId === row._id}
                              onClick={() => setCancelConfirmId(row._id)}
                            >
                              Cancel
                            </button>
                          )
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="quote-card settings-section-card team-hours-link-card">
        <div className="quote-section-title">Technician hours</div>
        <p className="settings-hint">
          Review clock punches, approve pay-period hours, and export payroll CSV in the dedicated hours approval
          workspace.
        </p>
        <Link to="/admin/settings?tab=hoursApproval" className="btn-primary team-hours-link-btn">
          Open hours approval →
        </Link>
      </section>

      <TeamDirectorySection refreshKey={directoryRefreshKey} />
    </div>
  );
}
